import React from 'react';
import {
  Activity,
  Anchor,
  Baby,
  Check,
  HeartPulse,
  Puzzle,
  Scissors,
  ShieldCheck,
  Smile,
  Sparkles,
  Stethoscope,
} from 'lucide-react';
import { treatments } from '../mockData';
import { openWhatsApp } from '@/lib/whatsapp';

const DISPLAY_ORDER = [1, 2, 3, 4, 5, 10, 6, 7, 8, 9];

const ICON_BY_CATEGORY = {
  Ortodontia: Smile,
  'Odontologia Estética': Sparkles,
  'Clínica Geral': Stethoscope,
  Endodontia: HeartPulse,
  'Odontologia Preventiva': ShieldCheck,
  'Implantes dentários': Anchor,
  Odontopediatria: Baby,
  'Cirurgia Oral': Scissors,
  'Tratamentos Funcionais': Activity,
  'Prótese Dentária': Puzzle,
};

const TAGLINE_BY_CATEGORY = {
  Ortodontia:
    'Dentes alinhados e sorriso bonito — aparelho fixo, estético ou quase invisível, com parcelamento facilitado.',
  'Odontologia Estética':
    'Sorriso mais branco e bonito: clareamento, facetas e lentes — resultado natural, sem exageros.',
  'Clínica Geral':
    'Consulta, limpeza e cuidado do dia a dia para você e sua família ficarem com a boca saudável.',
  Endodontia:
    'Tratamento de canal para acabar com a dor e salvar seu dente — sem sofrimento desnecessário.',
  'Odontologia Preventiva':
    'Cuidar antes de piorar sai mais barato: limpeza, flúor, selantes e orientação para toda a família.',
  'Implantes dentários':
    'Volte a comer e sorrir com segurança — dente novo que parece de verdade, com acompanhamento completo.',
  Odontopediatria:
    'Dentista para criança sem medo: atendimento calmo, prevenção e cuidado desde cedo.',
  'Cirurgia Oral':
    'Extração e pequenas cirurgias com segurança — a gente explica tudo antes e cuida depois.',
  'Tratamentos Funcionais':
    'Dor de cabeça, ranger os dentes ou dor na mandíbula? Placas e acompanhamento para aliviar.',
  'Prótese Dentária':
    'Dentes novos que encaixam bem e parecem naturais — coroas e próteses fixas ou removíveis.',
};

function openWhatsAppAbout(topic) {
  openWhatsApp({
    section: `tratamentos_${topic.replace(/\s+/g, '_')}`,
    message: `Olá! Vim pelo site e quero saber sobre ${topic}. Pode me passar mais informações?`,
  });
}

const TreatmentsSimple = () => {
  const ordered = DISPLAY_ORDER.map((id) => treatments.find((t) => t.id === id)).filter(Boolean);

  return (
    <section
      id="tratamentos"
      className="relative overflow-hidden bg-gradient-to-b from-brand-cream via-white to-brand-wash/40 py-24 md:py-28"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-24 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-brand-deep/[0.04] blur-3xl"
        aria-hidden
      />

      <div className="container relative mx-auto px-4">
        <header className="mx-auto mb-16 max-w-2xl text-center md:mb-20">
          <p className="mb-3 text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-brand-steel">
            O que fazemos
          </p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-brand-ink md:text-5xl">
            Tudo o que sua boca precisa, em <span className="text-brand-deep">um só lugar</span>
          </h2>
          <p className="mt-5 text-base font-light leading-relaxed text-brand-muted md:text-lg">
            Aparelho, implante, limpeza, canal e muito mais. A gente explica cada opção com calma, sem
            pressa e sem surpresa na hora de pagar.
          </p>
        </header>

        <div className="mx-auto grid max-w-7xl grid-cols-1 justify-items-center gap-8 md:grid-cols-2 xl:grid-cols-3 xl:gap-x-8 xl:gap-y-10">
          {ordered.map((block) => {
            const Icon = ICON_BY_CATEGORY[block.category] || Smile;
            const tagline = TAGLINE_BY_CATEGORY[block.category] || '';
            const topItems = block.items.slice(0, 3);
            const extra = Math.max(0, block.items.length - 3);

            return (
              <article
                key={block.id}
                className="group relative flex w-full max-w-md flex-col overflow-hidden rounded-[1.35rem] border border-brand-ink/[0.07] bg-white text-left shadow-[0_4px_24px_-4px_rgba(12,21,36,0.08)] ring-1 ring-brand-ink/[0.02] transition duration-300 hover:-translate-y-1 hover:border-brand-deep/18 hover:shadow-brand-xl"
              >
                <div
                  className="h-1 w-full bg-gradient-to-r from-brand-gold/90 via-brand-deep to-brand-gold/90 opacity-90"
                  aria-hidden
                />
                <div className="flex flex-1 flex-col p-7 md:p-8">
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-wash to-brand-cream text-brand-deep shadow-sm ring-1 ring-brand-ink/8 transition group-hover:from-brand-cream group-hover:to-brand-wash">
                      <Icon className="h-7 w-7" strokeWidth={1.4} />
                    </div>
                    <span className="shrink-0 rounded-full border border-brand-ink/10 bg-brand-cream/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-brand-steel">
                      Atendemos aqui
                    </span>
                  </div>

                  <h3 className="font-display text-2xl font-semibold leading-snug tracking-tight text-brand-ink">
                    {block.category}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-muted">{tagline}</p>

                  <div className="mt-6 flex flex-1 flex-col border-t border-brand-ink/8 pt-6">
                    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-deep/80">
                      O que fazemos
                    </p>
                    <ul className="space-y-4">
                      {topItems.map((item) => (
                        <li key={item.title} className="flex gap-3">
                          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-deep text-white shadow-sm">
                            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-brand-ink">{item.title}</p>
                            <p className="mt-1 text-xs leading-relaxed text-brand-muted">
                              {item.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {extra > 0 ? (
                      <p className="mt-4 text-xs italic text-brand-muted">
                        + {extra}{' '}
                        {extra === 1 ? 'serviço a mais' : 'serviços a mais'} — a gente combina na consulta.
                      </p>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={() => openWhatsAppAbout(block.category)}
                    className="mt-7 w-full rounded-full border-2 border-brand-deep/20 bg-transparent py-3.5 text-sm font-semibold text-brand-deep transition hover:border-brand-deep hover:bg-brand-deep hover:text-white"
                  >
                    Tirar dúvidas no WhatsApp
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        <footer className="mx-auto mt-16 max-w-xl text-center md:mt-20">
          <p className="text-sm text-brand-muted">
            Não sabe por onde começar? Chame no WhatsApp — avaliação sem compromisso e parcelamento
            facilitado.
          </p>
          <button
            type="button"
            onClick={() => openWhatsAppAbout('avaliação e orçamento')}
            className="mt-6 inline-flex rounded-full bg-brand-deep px-10 py-4 text-sm font-semibold text-white shadow-md transition hover:bg-brand-ink hover:shadow-brand-lg"
          >
            Falar com a clínica
          </button>
        </footer>
      </div>
    </section>
  );
};

export default TreatmentsSimple;
